<script>
import { mapStores } from "pinia";

import { isDownArrow, isEscape, isMouse, isTab, isUpArrow } from "@/common/event-helpers";
import NavbarItem from "@/components/header/NavbarItem.vue";
import { useSiteStateStore } from "@/stores";

export default {
  name: "GenericNavbar",
  components: {
    NavbarItem
  },
  props: {
    block: {
      type: String,
      default: "navbar"
    },
    triggerId: {
      type: String,
      default: undefined
    },
    items: {
      type: Array,
      required: true
    }
  },
  emits: ["hide", "toggle"],
  data() {
    return {
      currentItem: -1,
      vcoConfig: {
        handler: this.vcoHandler,
        middleware: this.clickOutsideMiddleware,
        events: ["dblclick", "click", "keydown"]
      }
    };
  },
  computed: {
    ...mapStores(useSiteStateStore),

    topOffset() {
      const baseOffset = 44;
      const bannerHeight = 46;

      const offset = this.siteStateStore.hasBanner ? baseOffset + bannerHeight : baseOffset;

      return `${offset}px`;
    }
  },
  mounted() {
    this.initGlobalKeyboardEvents();
  },
  beforeUnmount() {
    this.deinitGlobalKeyboardEvents();
  },
  methods: {
    clickOutsideMiddleware(event) {
      if (isMouse(event)) {
        return true;
      }

      if (isEscape(event)) {
        return true;
      }

      return false;
    },

    initGlobalKeyboardEvents() {
      window.addEventListener("keyup", this.handleTab);
      window.addEventListener("keyup", this.handleEsc);
      window.addEventListener("keydown", this.handleArrowNavigation);
    },

    deinitGlobalKeyboardEvents() {
      window.removeEventListener("keyup", this.handleTab);
      window.removeEventListener("keyup", this.handleEsc);
      window.removeEventListener("keydown", this.handleArrowNavigation);
    },

    isItemVisible(item) {
      if (typeof item.visible === "function") {
        return item.visible();
      }

      // If no visible function, it will be visible by default.
      return true;
    },

    hideHandler() {
      this.$emit("hide");
    },

    vcoHandler() {
      this.$emit("toggle");
    },

    handleTab(event) {
      if (!isTab(event)) {
        return;
      }

      this.currentItem = this.getItemIndex(document.activeElement);

      // Don't hide when tabbing inside of the menu
      if (this.$refs.navbar.contains(document.activeElement)) {
        return;
      }

      // Don't hide if focus is gained on the menu button
      if (typeof this.triggerId !== "undefined" && document.activeElement.id === this.triggerId) {
        return;
      }

      this.hideHandler();
    },

    handleEsc(event) {
      if (isEscape(event)) {
        this.hideHandler();
      }
    },

    handleArrowNavigation(event) {
      if (!isDownArrow(event) && !isUpArrow(event)) {
        return;
      }

      // Make sure page doesn't scroll
      event.preventDefault();

      // If the focus is still on the menu that triggered the opening
      // We have to use the first (dummy) item in the navbar
      const currentEl =
        this.currentItem === -1 ? this.$refs.firstItem : document.activeElement.parentElement;

      // Set the current item based on which arrow the user presses
      this.currentItem = isDownArrow(event)
        ? this.getNextItem(currentEl)
        : this.getPrevItem(currentEl);

      // If the currentItem is and index in the menuitems, we focus the first element of the menuitem
      if (this.currentItem >= 0 && this.currentItem < this.$refs.items.length) {
        this.$refs.items[this.currentItem].$el.firstElementChild.focus();
      }
    },

    getItemIndex(el) {
      // Check which <li> contains the passed element
      // Create an array of true/false values for those <li> indices
      // Get the indexOf of the one with "true" or -1 of no one has that el
      return this.$refs.items.map((item) => item.$el.contains(el)).indexOf(true);
    },

    getNextItem(el) {
      const nextSibling = el.nextElementSibling;

      // If we reached the end of the parent container, then we start from the 1st element,
      // Effectively being able to cycle through the list over and over
      if (!nextSibling) {
        return this.getNextItem(el.parentElement.firstElementChild);
      }

      // Don't include deactivated items, so
      // go to the next sibling (do this until an active item is found)
      if (
        nextSibling.hasAttribute("data-disabled") &&
        nextSibling.getAttribute("data-disabled") === "true"
      ) {
        return this.getNextItem(nextSibling);
      }

      return this.getItemIndex(nextSibling);
    },

    getPrevItem(el) {
      const prevSibling = el.previousElementSibling;

      if (!prevSibling) {
        return this.getPrevItem(el.parentElement.lastElementChild);
      }

      if (
        prevSibling.hasAttribute("data-disabled") &&
        prevSibling.getAttribute("data-disabled") === "true"
      ) {
        return this.getPrevItem(prevSibling);
      }

      return this.getItemIndex(prevSibling);
    }
  }
};
</script>

<template>
  <div ref="navbar" v-click-outside="vcoConfig" role="menu" :class="block">
    <div :class="`${block}__nav`">
      <ul class="list-unstyled mb-0">
        <li ref="firstItem" class="d-none" aria-disabled="true" data-disabled="true"></li>
        <template v-for="(navItem, index) in items">
          <NavbarItem
            v-if="isItemVisible(navItem)"
            :key="index"
            ref="items"
            tag="li"
            role="menuitem"
            :item="navItem"
            :block="block"
            :class="`${block}__item`"
            :link-class="`${block}__link`"
            :button-class="`${block}__button`"
            @click="hideHandler()"
          />
        </template>
        <li class="d-none" aria-disabled="true" data-disabled="true"></li>
      </ul>
      <slot />
    </div>
  </div>
</template>

<style scoped lang="less">
// Top is set dynamically depending on whether there's a banner on the page, in order to offset it
// All other styles are in `@/less/components/navbar.less`
.navbar {
  top: v-bind("topOffset");
}
</style>
